import { activityFlatsOfModels } from '@components/pages/activity-log/services/activity-log-flat.util';
import { BadgeInfo } from '@models/badge.model';
import { PaginatedResponse } from '@services/api/generic-api-client.service';
import { IApiQueryOptions, IOrderOptions } from '@services/api/models/api-query-params.model';
import { ParkingRequestClientService } from '@services/api/parking-request/parking-request-client.service';
import { IParkingRequestModel } from '@services/api/parking-request/parking-request-model.interface';
import { IPaymentTransactionModel } from '@services/api/payment-transaction/payment-transaction-model.interface';
import { BookingInfoService } from '@services/util/booking-info.service';
import { FlatInfoService } from '@services/util/flat-info.service';
import { LogService } from '@services/util/log.service';
import { parseISO } from 'date-fns';
import { catchError, map, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { ParkingRequestFilter } from '../models/parking-request-filter.model';
import { PaginatedParkingRequests, ParkingRequestRow } from '../models/parking-request.model';

@Injectable({
  providedIn: 'root'
})
export class ParkingRequestService {
  constructor(
    private readonly parkingRequestClient: ParkingRequestClientService,
    private readonly bookingInfoService: BookingInfoService,
    private readonly flatInfoService: FlatInfoService,
    private readonly log: LogService
  ) {}

  /**
   * Get paginated parking requests with optional sorting and filtering
   *
   * @param page Current page number (1-based)
   * @param itemsPerPage Number of items per page
   * @param sortField Field to sort by
   * @param sortOrder Sort direction
   * @param filter Optional filters
   * @returns Observable with paginated parking requests
   */
  getParkingRequests(
    page: number = 1,
    itemsPerPage: number = 30,
    sortField: string | null = null,
    sortOrder: 'asc' | 'desc' | null = null,
    filter?: ParkingRequestFilter
  ): Observable<PaginatedParkingRequests> {
    this.log.debug('Fetching parking requests with params:', { page, itemsPerPage, sortField, sortOrder, filter });

    // Map frontend field names to API field names for sorting
    const fieldMappings: Record<string, string> = {
      createdAt: 'created_at',
      startDate: 'start_date',
      endDate: 'end_date',
      licensePlate: 'license_plate',
      paid: 'paid'
    };

    // Define the order object based on sorting parameters
    let orderOptions: IOrderOptions = { created_at: 'desc' }; // Default sorting

    if (sortField && sortOrder) {
      // Use the mapped API field name if available, otherwise use the original
      const apiField = fieldMappings[sortField] || sortField;
      orderOptions = { [apiField]: sortOrder };
      this.log.debug(`Mapping sort field ${sortField} to API field ${apiField}`);
    }

    // Prepare filter objects
    const filterObj: Record<string, any> = {};
    const dateFilterObj: Record<string, any> = {};

    // Apply advanced filter options if provided
    if (filter) {
      // Mapping the filter fields to API field names
      if (filter.parkingPlaceId) {
        filterObj['parkingPlace.id'] = filter.parkingPlaceId;
      }

      if (filter.licensePlate) {
        filterObj['license_plate'] = filter.licensePlate;
      }

      if (filter['booking.name']) {
        filterObj['booking.name'] = filter['booking.name'];
      }

      if (filter.paid !== undefined) {
        filterObj['paid'] = filter.paid;
      }

      // Handle date filters
      if (filter.startDate) {
        dateFilterObj['start_date'] = filter.startDate;
      }

      if (filter.endDate) {
        dateFilterObj['end_date'] = filter.endDate;
      }

      this.log.debug('Prepared filter options:', { filterObj, dateFilterObj });
    }

    // Build query options for the API
    const options: IApiQueryOptions = {
      pagination: {
        page,
        itemsPerPage
      },
      order: orderOptions
    };

    // Only add filters and dateFilters if they have values
    if (Object.keys(filterObj).length > 0) {
      options.filters = filterObj;
    }

    if (Object.keys(dateFilterObj).length > 0) {
      options.dateFilters = dateFilterObj;
    }

    this.log.debug('Applied query options:', options);

    // Use the standard getItems method which now handles date filtering
    const request$ = this.parkingRequestClient.getItems('', options);

    return request$.pipe(
      map((response: PaginatedResponse<IParkingRequestModel>) => {
        // Transform API models to UI models
        const transformedRequests = response.items.map((request) => this.mapToParkingRequestRow(request));

        return {
          items: transformedRequests,
          totalCount: response.totalItems || 0,
          page,
          itemsPerPage
        };
      }),
      catchError((error) => {
        this.log.error('Error fetching parking requests:', error);
        return of({
          items: [],
          totalCount: 0,
          page,
          itemsPerPage
        });
      })
    );
  }

  /**
   * Delete a parking request
   *
   * @param id ID of the parking request to delete
   * @returns Observable with boolean indicating success
   */
  deleteParkingRequest(id: number): Observable<boolean> {
    this.log.debug('Deleting parking request with ID:', id);

    return this.parkingRequestClient.deleteItem(id).pipe(
      map(() => true),
      catchError((error) => {
        this.log.error('Error deleting parking request:', error);
        return of(false);
      })
    );
  }

  /**
   * Map API parking request model to UI parking request row model
   * @param request API parking request model
   * @returns UI parking request row model
   */
  generatePaymentTransaction(parkingRequestId: number): Observable<IPaymentTransactionModel> {
    this.log.debug('Generating payment transaction for parking request:', parkingRequestId);
    return this.parkingRequestClient.generatePaymentTransaction(parkingRequestId);
  }

  private extractTransactionCode(transactions?: IPaymentTransactionModel[]): string | undefined {
    if (!transactions || transactions.length === 0) return undefined;
    const embedded = transactions.filter((t): t is IPaymentTransactionModel => typeof t === 'object' && !!t.transaction_code);
    if (embedded.length === 0) return undefined;
    const unpaid = embedded.find((t) => !t.paid);
    return unpaid ? unpaid.transaction_code : embedded[0].transaction_code;
  }

  private mapToParkingRequestRow(request: IParkingRequestModel): ParkingRequestRow {
    // Create badge info for payment transaction
    const paymentTransaction =
      request.paymentTransactions && request.paymentTransactions.length > 0
        ? new BadgeInfo(
            request.paymentTransactions[0].id!,
            `Transaction #${request.paymentTransactions[0].id}`,
            'bg-yellow-200 text-yellow-900',
            'pi pi-credit-card'
          )
        : new BadgeInfo(0, 'No Payment', 'bg-gray-200 text-gray-700', 'pi pi-times-circle');

    // Get building and parking place names from the parkingPlace object
    const buildingName = request.parkingPlace?.building?.name || 'Unknown';
    const parkingPlaceName = request.parkingPlace?.name || 'Unknown';
    const parkingPlaceId = request.parkingPlace?.id || 0;

    // Create FlatInfo for the parking place, specifying it is a parking place
    const parkingPlaceInfo = this.flatInfoService.createFlatInfo(request.parkingPlace, true);

    // Extract invoice information - handle both object and string formats
    let invoiceId: number | null = null;
    let invoiceDisplay: string | null = null;
    if (request.invoice) {
      if (typeof request.invoice === 'object' && 'id' in request.invoice) {
        const invoiceObj = request.invoice as any;
        invoiceId = invoiceObj.id || null;
        invoiceDisplay = invoiceObj.invoice_number || (invoiceId ? `#${invoiceId}` : null);
      } else if (typeof request.invoice === 'string') {
        invoiceDisplay = request.invoice;
      }
    }

    const transactionCode = this.extractTransactionCode(request.paymentTransactions);
    const bookingNationality = request.booking?.nationality;

    return {
      id: request.id!,
      createdAt: request.created_at ? parseISO(request.created_at) : new Date(),
      booking: this.bookingInfoService.createBookingInfo(request.booking),
      bookingFlats: activityFlatsOfModels(request.booking?.flats),
      startDate: request.start_date ? parseISO(request.start_date) : new Date(),
      endDate: request.end_date ? parseISO(request.end_date) : new Date(),
      buildingName,
      parkingPlaceId,
      parkingPlace: parkingPlaceName,
      parkingPlaceInfo,
      licensePlate: request.license_plate || '',
      paid: request.paid || false,
      paymentTransaction,
      transactionCode,
      bookingNationality,
      invoiceId,
      invoiceDisplay
    };
  }
}
